///
/// EarthBrowser
///
/// Created by Matt Giger on 3/29/2024
/// Copyright (c) 2024 Geodata Labs
///

import { type NodeBase } from "@stovokor/engine/NodeBase"
import { type Stovokor } from "@stovokor/Stovokor"
import { StovokorContext } from "@stovokor/StovokorContext"
import { KernelFrameTask, type FrameTask } from "@stovokor/tasks/FrameTask"
import { KernelTimerTask, type TimerTask } from "@stovokor/tasks/TimerTask"

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export interface ActivityBase {
	start?(): void
	finish?(): void
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export abstract class ActivityBase {
	public engine!: Stovokor
	public context: StovokorContext
	protected parent?: ActivityBase
	protected activites: ActivityBase[] = []
	protected timers: number[] = []
	protected frameTasks: number[] = []
	protected _worldNodes: NodeBase[] = []
	protected _uiNodes: NodeBase[] = []

	constructor(context: StovokorContext) {
		this.context = context
	}

	// Called when being destroyed
	public destroy(): void {
		this._worldNodes = []
		this._uiNodes = []
		const taskManager = this.engine.taskManager
		this.frameTasks.forEach(taskId => taskManager.removeFrameTask(taskId))
		this.timers.forEach(timerId => taskManager.removeTimerTask(timerId))
		this.activites.forEach(activity => activity.destroy())
	}

	public setEngine(engine: Stovokor): void {
		this.engine = engine
	}

	public get worldNodes(): NodeBase[] {
		return this._worldNodes
	}
	public set worldNodes(nodes: NodeBase[]) {
		this._worldNodes = nodes
	}
	public get uiNodes(): NodeBase[] {
		return this._uiNodes
	}
	public set uiNodes(nodes: NodeBase[]) {
		this._uiNodes = nodes
	}

	public addViewportNodes(): void {
		this.engine.world.addNode(this._worldNodes)
		this.engine.interface.addNode(this._uiNodes)
	}

	protected removeViewportNodes(): void {
		this.engine.world.removeNode(this._worldNodes)
		this.engine.interface.removeNode(this._uiNodes)
	}

	public exit(): void {
		this.parent?.removeActivity(this)
	}

	public addTimer(delay: number | TimerTask, frequency: number = 0, action: () => void = () => {}, count: number = 0): number {
		let timerId = 0
		if(typeof delay === 'number') {
			const task = new KernelTimerTask(delay, frequency, action, count)
			timerId = this.engine.taskManager.addTimerTask(task)
		} else {
			timerId = this.engine.taskManager.addTimerTask(delay)
		}
		this.timers.push(timerId)
		return timerId
	}

	public removeTimer(timerId: number): void {
		const index = this.timers.indexOf(timerId)
		if(index >= 0) {
			this.engine.taskManager.removeTimerTask(timerId)
			this.timers.splice(index, 1)
		}
	}

	public addFrameTask(action: (() => void) | FrameTask): number {
		let taskId = 0
		if(typeof action === 'function') {
			const task = new KernelFrameTask(action)
			taskId = this.engine.taskManager.addFrameTask(task)
		} else {
			taskId = this.engine.taskManager.addFrameTask(action)
		}
		this.frameTasks.push(taskId)
		return taskId
	}

	public removeFrameTask(taskId: number): void {
		const index = this.frameTasks.indexOf(taskId)
		if(index >= 0) {
			this.engine.taskManager.removeFrameTask(taskId)
			this.frameTasks.splice(index, 1)
		}
	}

	public addActivity(activity: ActivityBase): void {
		this.activites.push(activity)
		activity.parent = this
		activity.setEngine(this.engine)
		activity.start?.()
		activity.addViewportNodes()
	}

	protected activityRemoved(_activity: ActivityBase): void {
		// override this to handle child activity removal
	}

	public removeActivity(activity: ActivityBase): void {

		// remove all children first
		activity.activites.forEach(child => activity.removeActivity(child))

		const index = this.activites.indexOf(activity)
		if(index >= 0) {
			activity.removeViewportNodes()
			activity.finish?.()
			activity.destroy()
			activity.parent?.activityRemoved(this)
			activity.parent = undefined
			this.activites.splice(index, 1)
		}
	}
}